import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter, Inject,
  Input, OnChanges, OnDestroy, OnInit, Optional,
  Output, SimpleChanges
} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import {FormControl, Validators} from "@angular/forms";
import {onlyNumberValidator} from "../../../validators";
import {DestroyStreamService} from "../../../services";
import {KEYBOARD_KEY_PRESSED} from "../../../providers";
import {takeUntil} from "rxjs/operators";
import {INT_TESTING_DATA_MARKERS_TOKEN} from "../../../tokens";
import {IntTestingDataMarkers} from "../../../types";
import {INT_DEFAULT_PAGINATOR_TEST_MARKERS_TOKEN} from "../tokens";
import {IIntDefaultPaginatorTestMarker} from "../types";

@Component({
  selector: 'int-default-paginator',
  styleUrls: ['./int-default-paginator.component.scss'],
  templateUrl: './int-default-paginator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService
  ]
})
export class IntDefaultPaginatorComponent implements OnDestroy, OnInit, OnChanges {

  @Input() perPageList = [10, 20, 30]
  @Input() perPage = 10;
  @Input() currentPage = 1;
  @Input() recordsAmount: number = 30;

  @Output() perPageEvent = new EventEmitter<number>();
  @Output() pageToggleEvent = new EventEmitter<number>();


  isShowPerPageDropdown$ = new BehaviorSubject<boolean>(false);
  isShowPageAmountDropdown$ = new BehaviorSubject<boolean>(false);

  control: FormControl = new FormControl(null);

  isSearchDropVisible = false;

  constructor(
    private readonly destroy$: DestroyStreamService,
    @Inject(KEYBOARD_KEY_PRESSED) private readonly pressedStreams: Observable<string>,
    @Optional()
    @Inject(INT_TESTING_DATA_MARKERS_TOKEN) readonly testingMarkers: IntTestingDataMarkers,
    @Optional()
    @Inject(INT_DEFAULT_PAGINATOR_TEST_MARKERS_TOKEN) readonly testAttrsValues: IIntDefaultPaginatorTestMarker
  ) {
    this.pressedStreams.pipe(takeUntil(this.destroy$)).subscribe((key) => {
      if(key === 'Enter' && this.isSearchDropVisible) {
        this.searchPage();
      }
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if(this.control && this.recordsAmount && this.perPage) {
      this.control.clearValidators();
      this.control.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(this.pagesAmount),
        onlyNumberValidator()
      ]);
      this.control.updateValueAndValidity();
    }
  }

  ngOnInit() {

  }

  get isSearchValid() {
    return this.control?.valid;
  }

  get isNotFirstPage() {
    return this.currentPage !== 1;
  }

  get isNotLastPage() {
    return this.currentPage < this.pagesAmount;
  }

  get isPossibleToShow(): boolean {
    return this.perPage < this.recordsAmount;
  }

  get pagesAmount(): number {
    return Math.ceil(this.recordsAmount / this.perPage)
  }

  get pagesAmountList(): Array<number> {
    return Array(this.pagesAmount).fill(0).map((_, i) => i + 1);
  }

  setDropVisibleStatus(status: boolean) {
    this.isSearchDropVisible = status;
  }

  searchPage() {
    const value = this.control.value;
    if(this.control.valid && !isNaN(parseInt(value))) {
      this.togglePageEvent(parseInt(value));
    }
  }

  goToPreviousPage() {
    const nextPage = this.currentPage - 1;
    this.togglePageEvent(nextPage <= 0 ? 0 : nextPage);
  }

  goToNextPage() {
    const nextPage = this.currentPage + 1;
    this.togglePageEvent(nextPage >= this.pagesAmount ? this.pagesAmount : nextPage);
  }

  togglePerPageAmountDropdown() {
    this.isShowPerPageDropdown$.next(!this.isShowPerPageDropdown$.value)
  }

  closePerPageDropdown() {
    this.isShowPerPageDropdown$.next(false);
  }

  changePerPageAmount(amount: number) {
    this.perPage = amount;
    this.perPageEvent.emit(amount);
    this.closePerPageDropdown();
  }

  togglePageEvent(amount: number) {
    this.pageToggleEvent.emit(amount);
    this.closePageAmount();
  }
  togglePageDropdown() {
    this.isShowPageAmountDropdown$.next(!this.isShowPageAmountDropdown$.value);
  }
  closePageAmount() {
    this.isShowPageAmountDropdown$.next(false);
  }
  ngOnDestroy() {
    this.isShowPageAmountDropdown$.complete();
    this.isShowPerPageDropdown$.complete();
  }
}
